function isAmount (value) {
  return Number.isFinite(value) && value >= 0
}

function isMonthlyPlan (plan) {
  return plan.type === 'recurring' &&
    plan.interval === 'month' &&
    (plan.intervalCount || 1) === 1
}

function isAnnualPlan (plan) {
  return plan.type === 'recurring' &&
    plan.interval === 'year' &&
    (plan.intervalCount || 1) === 1
}

// Stripe amounts are in the currency's smallest unit, except for these
// zero-decimal currencies, where 39000 VND is ₫39,000, not ₫390.
// https://docs.stripe.com/currencies#zero-decimal
const ZERO_DECIMAL_CURRENCIES = new Set([
  'bif', 'clp', 'djf', 'gnf', 'jpy', 'kmf', 'krw', 'mga',
  'pyg', 'rwf', 'ugx', 'vnd', 'vuv', 'xaf', 'xof', 'xpf'
])

function formatMoney (unitAmount, currency) {
  if (!isAmount(unitAmount) || !currency) return null

  const code = currency.toLowerCase()
  try {
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: code.toUpperCase(),
      currencyDisplay: 'narrowSymbol',
      maximumFractionDigits: ZERO_DECIMAL_CURRENCIES.has(code) ? 0 : 2
    }).format(ZERO_DECIMAL_CURRENCIES.has(code) ? unitAmount : unitAmount / 100)
  } catch (_) {
    return null
  }
}

function findMonthlyBenchmark (plan, plans) {
  return plans.find((candidate) =>
    candidate.priceId !== plan.priceId &&
    candidate.currency === plan.currency &&
    isMonthlyPlan(candidate) &&
    isAmount(candidate.unitAmount) &&
    candidate.unitAmount > 0
  ) || null
}

function percentSaved (referenceAmount, actualAmount) {
  if (!isAmount(referenceAmount) || referenceAmount <= 0 || !isAmount(actualAmount)) return 0
  return Math.max(0, Math.round(((referenceAmount - actualAmount) / referenceAmount) * 100))
}

function annualPresentation (plan, monthly) {
  const referenceAmount = monthly.unitAmount * 12
  const savingsAmount = referenceAmount - plan.unitAmount
  const savingsPercent = percentSaved(referenceAmount, plan.unitAmount)
  if (savingsAmount <= 0 || savingsPercent <= 0) return null

  const monthlyEquivalent = formatMoney(plan.unitAmount / 12, plan.currency)
  const formattedSavings = formatMoney(savingsAmount, plan.currency)

  return {
    badge: `Save ${savingsPercent}%`,
    summary: monthlyEquivalent && formattedSavings
      ? `${monthlyEquivalent}/month · ${formattedSavings} saved each year`
      : `Save ${savingsPercent}% compared with monthly`,
    savingsPercent,
    savingsAmount,
    monthlyEquivalentAmount: plan.unitAmount / 12
  }
}

function lifetimePresentation (plan, monthly) {
  const referenceAmount = monthly.unitAmount * 12
  const savingsAmount = referenceAmount - plan.unitAmount
  const savingsPercent = percentSaved(referenceAmount, plan.unitAmount)
  const breakEvenMonths = Math.max(1, Math.ceil(plan.unitAmount / monthly.unitAmount))

  return {
    badge: savingsPercent > 0 ? `Save ${savingsPercent}% in year 1` : 'Pay once',
    summary: `Pays for itself in ${breakEvenMonths} ${breakEvenMonths === 1 ? 'month' : 'months'} · no renewals`,
    savingsPercent,
    savingsAmount: Math.max(0, savingsAmount),
    breakEvenMonths
  }
}

function buildPlanPresentation (plan, plans) {
  if (!plan) {
    return {
      badge: null,
      summary: null,
      savingsPercent: 0,
      savingsAmount: 0
    }
  }

  const fallback = {
    badge: plan.type === 'one_time' ? 'Pay once' : null,
    summary: plan.hint || null,
    savingsPercent: 0,
    savingsAmount: 0
  }

  if (!Array.isArray(plans) || !isAmount(plan.unitAmount)) return fallback

  const monthly = findMonthlyBenchmark(plan, plans)
  if (!monthly) return fallback

  if (isAnnualPlan(plan)) return annualPresentation(plan, monthly) || fallback
  if (plan.type === 'one_time') return lifetimePresentation(plan, monthly)
  return fallback
}

module.exports = {
  buildPlanPresentation,
  formatMoney,
  percentSaved
}
